import { isPresent, isWebLink } from "@/lib/utils"
import vuexStore from "@/store"
import router from "@/router"
import i18n from "@/store/i18n"
import ApiClient from "@/lib/api-client"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { formatAmount } from "@/lib/checkout-utils"
import { pageDataToProps } from "@shared/page-data"
import { Product } from "@shared/types"
import { toDate } from "@/lib/date-utils"
import { getCachedPageByPath } from "@/lib/page-cache"
import fetchPageData from "@/lib/fetch-page-data"
import {
  Row,
  QueryOptions,
  DeleteQuery,
  TrackEventPayload,
  MvtRoute,
  NavigateOptions,
  Occurrence,
  ScheduleQueryOptions,
  ScheduleAddOptions,
  ProductDetails,
  PurchaseResult,
  Page,
  PagesQueryOptions,
  PagesQueryResult,
  SavedListOptions,
  ProgressStatus,
  StartOptions,
  HistoryOptions,
  Completion,
  DateInput
} from "./types"
import { EventManager, MVT_EVENTS } from "./event-manager"
import { HydrationGuard } from "./hydration-guard"
import { NativeBridge } from "./native-bridge"
import { ReadCache } from "./read-cache"
import { ProgressBatch } from "./progress-batch"

export { MVT_EVENTS } from "./event-manager"

// On 422 validation failure, caught errors are augmented with `fieldErrors`:
//
//   try { await mvt.tables.meals.create(value) }
//   catch (err) {
//     if (err.fieldErrors) {
//       // { calories: ["must be a number"], name: ["is required"] }
//     } else throw err
//   }
//
// Phrasing is sourced from `lib.validation.*` in the locale files.

type ValidationCode = { code: string; expected?: any }

type ApiError = {
  response?: { status?: number; data?: Record<string, unknown> }
  fieldErrors?: Record<string, string[]>
}

function renderCode({ code, expected }: ValidationCode): string {
  const value = Array.isArray(expected)
    ? expected.join(", ")
    : String(expected ?? "")
  const key = `lib.validation.${code}`
  const finalKey = i18n.te(key) ? key : "lib.validation.unknown"
  return i18n.t(finalKey, { expected: value }) as string
}

function attachFieldErrors(err: any): never {
  const data = err?.response?.data
  if (data?.reason === "validation_error" && data.errors) {
    err.fieldErrors = Object.fromEntries(
      Object.entries<ValidationCode[]>(data.errors).map(([field, list]) => [
        field,
        list.map(renderCode)
      ])
    )
  }
  throw err
}

const readCache = new ReadCache()

class ApiService {
  static async post(endpoint: string, body: Record<string, any>) {
    try {
      const response = await readCache.write(() =>
        ApiClient.post(endpoint, body)
      )
      return response.data
    } catch (err) {
      attachFieldErrors(err)
    }
  }

  static async get(endpoint: string, params: Record<string, any>) {
    return await readCache.get(endpoint, params)
  }

  static async put(endpoint: string, body: Record<string, any>) {
    try {
      const response = await readCache.write(() =>
        ApiClient.put(endpoint, body)
      )
      return response.data
    } catch (err) {
      attachFieldErrors(err)
    }
  }

  static async delete(endpoint: string, params: Record<string, any>) {
    try {
      const response = await readCache.write(() =>
        ApiClient.delete(endpoint, { params })
      )
      return response.data
    } catch (err) {
      attachFieldErrors(err)
    }
  }
}

class Table {
  constructor(private readonly slug: string) {}

  private get endpoint() {
    return `/tables/${this.slug}`
  }

  async create(input: any | Row[], created_at?: Date) {
    const data = Array.isArray(input) ? input : { value: input, created_at }
    return await ApiService.post(this.endpoint, { data })
  }

  async update(id: string, value: any) {
    return await ApiService.put(this.endpoint, { id, value })
  }

  async delete(query?: DeleteQuery) {
    const params: DeleteQuery = {}

    if (query?.id) params.id = query.id
    if (query?.where) params.where = query.where

    return await ApiService.delete(this.endpoint, params)
  }

  async query(options: QueryOptions = {}) {
    const { all_members, timezone, ...query } = options
    try {
      return await ApiService.get(this.endpoint, {
        query: {
          ...query,
          ...(timezone ? { timezone } : {})
        },
        ...(all_members ? { all_members: true } : {})
      })
    } catch (err) {
      if ((err as ApiError).response?.status === 404) return { results: [] }
      throw err
    }
  }

  async first(options: QueryOptions = {}) {
    const res = await this.query({ ...options, limit: 1 })
    return res.results[0] ?? null
  }
}

const eventManager = new EventManager()

let purchaseSeq = 0

class ProductsApi {
  // Member-scoped details (price/availability can vary per member), so the
  // cache is dropped on logout and refetched lazily per id on first read.
  private readonly cache = new Map<number, ProductDetails>()

  constructor() {
    eventManager.on(MVT_EVENTS.LOGGED_OUT, () => this.cache.clear())
  }

  hasPurchased(id: number): boolean {
    return vuexStore.getters["auth/hasPurchasedProduct"](id)
  }

  async getDetails(id: number): Promise<ProductDetails> {
    const cached = this.cache.get(id)
    if (cached) return cached

    const product: Product = await vuexStore.dispatch(
      "products/fetchProduct",
      id
    )
    const details = this.toDetails(product)
    this.cache.set(id, details)
    return details
  }

  async purchase(id: number): Promise<PurchaseResult> {
    if (this.hasPurchased(id)) return { status: "paid" }

    const token = `mvt-purchase-${++purchaseSeq}`

    return new Promise<PurchaseResult>(resolve => {
      let settled = false

      const settle = (result: PurchaseResult) => {
        if (settled) return
        settled = true
        EventBus.$off(EVENTS.CHECKOUT_COMPLETED, onCompleted)
        EventBus.$off(EVENTS.CHECKOUT_CLOSED, onClosed)
        resolve(result)
      }

      // The token identifies this invocation so a stale close from a prior
      // checkout of the same product (the kept-alive Checkout deactivating as
      // the next one opens) cannot settle it. On close, ownership decides the
      // outcome: some checkout exits grant ownership without a `completed`
      // event (e.g. an already-active purchase confirmed server-side), so
      // ownership-now-true on close is a success.
      const onCompleted = (_id: number, eventToken?: string) => {
        if (eventToken !== token) return
        settle({ status: "paid" })
      }
      const onClosed = (_id: number, eventToken?: string) => {
        if (eventToken !== token) return
        settle({ status: this.hasPurchased(id) ? "paid" : "cancelled" })
      }

      EventBus.$on(EVENTS.CHECKOUT_COMPLETED, onCompleted)
      EventBus.$on(EVENTS.CHECKOUT_CLOSED, onClosed)

      EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, { productIds: [id], token })
    })
  }

  private toDetails(product: Product): ProductDetails {
    return {
      id: product.id,
      name: product.title,
      priceLabel: this.priceLabel(product)
    }
  }

  private priceLabel(product: Product): string {
    if (!isPresent(product.price)) return ""

    const symbol = product.symbol || "$"
    const currency = product.currency
      ? ` ${product.currency.toUpperCase()}`
      : ""
    return `${symbol}${formatAmount(product.price)}${currency}`
  }
}

const loggedIn = (): boolean => isPresent(vuexStore.getters["auth/currentUser"])

const codedError = (code: string, message: string): Error => {
  const error: any = new Error(message)
  error.code = code
  return error
}

const notAuthenticated = (): Error =>
  codedError("not_authenticated", "Not signed in")

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

function toIsoDate(value: DateInput, label: string): string {
  const date = new Date(value)
  if (isNaN(date.getTime())) {
    throw codedError("invalid_date", `Invalid ${label}: ${String(value)}`)
  }
  return date.toISOString()
}

// The host already caches every visited page by path (IndexedDB + the inlined
// FIT_initialState), so a sync lookup usually avoids a round-trip.
class PagePaths {
  private readonly uuidByPath: Record<string, string> = {}
  private readonly unresolvable = new Set<string>()
  private readonly inFlight = new Map<string, Promise<string | null>>()
  private session = 0

  constructor() {
    const forget = () => {
      this.unresolvable.clear()
      this.session++
    }
    eventManager.on(MVT_EVENTS.LOGGED_IN, forget)
    eventManager.on(MVT_EVENTS.LOGGED_OUT, forget)
  }

  known(path: string): string | null {
    if (this.uuidByPath[path]) return this.uuidByPath[path]

    const inlined = window.FIT_initialState?.[path]?.props?.page_id
    return inlined ? (this.uuidByPath[path] = inlined) : null
  }

  async resolve(path: string): Promise<string> {
    const uuid = this.known(path) ?? (await this.lookup(path).catch(() => null))
    if (!uuid) throw codedError("not_found", `Unknown page: ${path}`)
    return uuid
  }

  // Reactive reads re-run this every render, so a path the server says has no
  // page is remembered. A lookup that threw is not — it can't be told apart
  // from a blip.
  lookup(path: string): Promise<string | null> {
    if (this.unresolvable.has(path)) return Promise.resolve(null)

    const existing = this.inFlight.get(path)
    if (existing) return existing

    const session = this.session
    const lookup = this.fetchUuid(path)
      .then(uuid => {
        // A lookup that started before a session change says nothing about this one.
        if (!uuid && session === this.session) this.unresolvable.add(path)
        return uuid
      })
      .finally(() => this.inFlight.delete(path))

    this.inFlight.set(path, lookup)

    return lookup
  }

  private async fetchUuid(path: string): Promise<string | null> {
    const cached = await getCachedPageByPath(path)
    let uuid = cached?.props?.page_id

    // Deliberately unguarded: a throw here must stay distinct from "no page".
    if (!uuid) uuid = (await fetchPageData(path))?.props?.page_id

    if (uuid) this.uuidByPath[path] = uuid
    return uuid ?? null
  }
}

const ACTIVE_KEY = "active"

class Progress {
  private readonly guard: HydrationGuard
  private readonly activeGuard: HydrationGuard
  private readonly batch = new ProgressBatch(page_ids =>
    ApiService.get("/progress/pages", { page_ids })
  )
  private activeContent: Array<Page & { id: string }> = []
  private activeFetchSeq = 0

  constructor(private readonly pagePaths: PagePaths) {
    const announce = () => eventManager.emit(MVT_EVENTS.PROGRESS_CHANGED)
    this.guard = new HydrationGuard(announce)
    this.activeGuard = new HydrationGuard(announce)

    const invalidate = () => {
      this.guard.clear()
      this.activeGuard.clear()
      this.activeFetchSeq++
      this.activeContent = []
    }
    eventManager.on(MVT_EVENTS.LOGGED_IN, invalidate)
    eventManager.on(MVT_EVENTS.LOGGED_OUT, invalidate)
    eventManager.on(MVT_EVENTS.PROGRESS_CHANGED, mutation => {
      if (mutation?.type === "pages/setPageSubscription") {
        this.activeGuard.forget(ACTIVE_KEY)
      } else if (mutation?.type === "pages/deletePageSubscription") {
        this.removeFromActive(mutation.payload)
      }
    })
  }

  status(path: string): ProgressStatus {
    const uuid = this.resolveUuid(path)

    if (uuid) this.hydrateProgress(path, uuid)

    // getPageProgress is not auth-scoped and the store slice survives a logout,
    // so a logged-out (or not-yet-refetched next-member) read would otherwise
    // surface the previous member's row. Gate it on the session.
    const progress =
      uuid && loggedIn() ? vuexStore.getters["pages/getPageProgress"](uuid) : {}
    const completed = uuid
      ? vuexStore.getters["pages/isPageComplete"](uuid)
      : false

    return {
      started: isPresent(progress.started_at),
      startedAt: progress.started_at ?? null,
      completed,
      completedAt: progress.completed_at ?? null,
      percent: progress.percent_complete ?? 0
    }
  }

  get active(): Page[] {
    if (!loggedIn()) return []

    this.hydrateActive()

    return this.activeContent
      .filter(item => !this.isComplete(item.id))
      .map(item => ({
        path: item.path,
        title: item.title ?? "",
        type: item.type
      }))
      .filter(page => isPresent(page.path))
  }

  destroy(): void {
    this.guard.destroy()
    this.activeGuard.destroy()
  }

  async start(path: string, options: StartOptions = {}): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const startDate = isPresent(options.startedAt)
      ? toIsoDate(options.startedAt, "startedAt")
      : undefined

    const pageId = await this.pagePaths.resolve(path)
    await readCache.write(() =>
      vuexStore.dispatch("pages/subscribe", { pageId, startDate })
    )
    await this.refetchActive()
  }

  async complete(path: string): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const pageId = await this.pagePaths.resolve(path)
    await readCache.write(() =>
      vuexStore.dispatch("pages/completePage", pageId)
    )
    // percent arrives only via this refetch, and its failure must not reject a
    // completion that already succeeded.
    await this.guard
      .run(path, () => this.batch.fetch(pageId))
      .catch(() => undefined)
  }

  // The member's completed pages in the window, newest first.
  async history(options: HistoryOptions): Promise<Completion[]> {
    if (!loggedIn()) throw notAuthenticated()

    const params: Record<string, string> = {
      from: this.toRangeBound(options.from, "from"),
      to: this.toRangeBound(options.to, "to")
    }
    if (isPresent(options.of)) params.of = options.of as string

    let data
    try {
      data = await ApiService.get("/progress/completions", params)
    } catch (err) {
      if ((err as ApiError).response?.status === 404) {
        throw codedError("not_found", `Unknown page: ${options.of}`)
      }
      throw err
    }

    return (data?.completions ?? [])
      .filter((completion: any) => isPresent(completion.path))
      .map((completion: any) => ({
        completedAt: completion.completed_at,
        page: {
          path: completion.path,
          title: completion.title ?? "",
          type: completion.type
        }
      }))
  }

  /**
   * Destructive: drops the member's enrolment and every progress record beneath
   * this page (the whole subtree), back to never-started. This is NOT a
   * per-page un-complete — there is no way to un-tick a single page.
   */
  async reset(path: string): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const pageId = await this.pagePaths.resolve(path)
    await readCache.write(() => vuexStore.dispatch("pages/unsubscribe", pageId))
    this.removeFromActive(pageId)
  }

  // Date-only bounds are inclusive UTC days: `to` expands to end-of-day so
  // same-day completions survive. Precise instants pass through unchanged.
  private toRangeBound(value: DateInput, bound: "from" | "to"): string {
    if (typeof value === "string" && DATE_ONLY.test(value)) {
      const suffix = bound === "to" ? "T23:59:59.999Z" : "T00:00:00.000Z"
      return toIsoDate(`${value}${suffix}`, bound)
    }
    return toIsoDate(value, bound)
  }

  private isComplete(uuid?: string): boolean {
    return isPresent(uuid)
      ? vuexStore.getters["pages/isPageComplete"](uuid)
      : false
  }

  private hydrateActive(): void {
    this.activeGuard.ensure(ACTIVE_KEY, () => this.fetchActive())
  }

  private async refetchActive(): Promise<void> {
    await this.activeGuard
      .run(ACTIVE_KEY, () => this.fetchActive())
      .catch(() => undefined)
  }

  private async fetchActive(): Promise<void> {
    const seq = ++this.activeFetchSeq

    const { active } = await ApiService.get("/progress/active", {})
    if (seq === this.activeFetchSeq && loggedIn()) {
      this.activeContent = Array.isArray(active) ? active : []
      eventManager.emit(MVT_EVENTS.PROGRESS_CHANGED)
    }
  }

  private removeFromActive(pageId?: string): void {
    if (!isPresent(pageId)) return
    this.activeContent = this.activeContent.filter(item => item.id !== pageId)
  }

  private resolveUuid(path: string): string | null {
    const known = this.pagePaths.known(path)
    if (known) return known

    this.hydrate(path)
    return null
  }

  // Resolves an unknown path, then loads its progress so a first off-page read
  // fills in (and re-renders via progress:changed) instead of staying a
  // false zero.
  private hydrate(path: string): void {
    this.guard.ensure(path, async () => {
      const uuid = await this.pagePaths.lookup(path)
      if (uuid && loggedIn()) await this.batch.fetch(uuid)
    })
  }

  // status() resolves the uuid synchronously (inlined/cached) but the progress
  // row may not be loaded — fetch it once so percent/completed_at aren't a
  // false zero for a page the member never navigated to. Shares the guard with
  // hydrate(), so a path resolved the slow way isn't fetched twice.
  private hydrateProgress(path: string, uuid: string): void {
    if (!loggedIn()) return

    this.guard.ensure(path, () => this.batch.fetch(uuid))
  }
}

type SavedAction = "pages/addPageBookmark" | "pages/removePageBookmark"

// Saving is intent, not attainment — deliberately separate from `progress`,
// which is enrolment and completion.
class Saved {
  private readonly guard: HydrationGuard

  constructor(
    private readonly pagePaths: PagePaths,
    private readonly pages: Pages
  ) {
    this.guard = new HydrationGuard(() =>
      eventManager.emit(MVT_EVENTS.SAVED_CHANGED)
    )

    const invalidate = () => this.guard.clear()
    eventManager.on(MVT_EVENTS.LOGGED_IN, invalidate)
    eventManager.on(MVT_EVENTS.LOGGED_OUT, invalidate)
  }

  destroy(): void {
    this.guard.destroy()
  }

  has(path: string): boolean {
    if (!loggedIn()) return false

    const uuid = this.pagePaths.known(path)
    if (!uuid) {
      this.hydrate(path)
      return false
    }

    return vuexStore.getters["pages/isPageBookmarked"](uuid)
  }

  // The member's saved pages, newest save first. Rides the content engine's
  // own saved filter, so it takes the same type/tag/paging options as any
  // other page query.
  async list(options: SavedListOptions = {}): Promise<Page[]> {
    if (!loggedIn()) return []

    const { results } = await this.pages.query({ ...options, saved: true })
    return results
  }

  async add(path: string): Promise<void> {
    await this.write("pages/addPageBookmark", path)
  }

  async remove(path: string): Promise<void> {
    await this.write("pages/removePageBookmark", path)
  }

  private async write(action: SavedAction, path: string): Promise<void> {
    if (!loggedIn()) throw notAuthenticated()

    const pageId = await this.pagePaths.resolve(path)
    await readCache.write(() => vuexStore.dispatch(action, pageId))
  }

  // Announces a newly resolved path so the read that asked for it re-runs.
  private hydrate(path: string): void {
    this.guard.ensure(path, async () => {
      const uuid = await this.pagePaths.lookup(path)
      if (uuid) eventManager.emit(MVT_EVENTS.SAVED_CHANGED)
    })
  }
}

class Schedule {
  constructor(private readonly pagePaths: PagePaths) {}

  async query({
    from,
    to,
    timezone,
    type,
    select
  }: ScheduleQueryOptions): Promise<Occurrence[]> {
    const filters = Array.isArray(type) ? type : type ? [type] : []
    const types = filters.length ? filters : [undefined]

    // from/to are whole calendar days; timezone is forwarded only when given so
    // the server applies its own member/account fallback otherwise.
    const range: Record<string, any> = {
      sdk: 1,
      from: toDate(from, "YYYY-MM-DD"),
      to: toDate(to, "YYYY-MM-DD"),
      ...(timezone ? { timezone } : {}),
      ...(select?.length ? { select } : {})
    }

    const batches = await Promise.all(
      types.map(filter =>
        this.fetchAll({ ...range, ...(filter ? { filter } : {}) })
      )
    )

    // Fanning out per type can surface the same occurrence twice; dedupe by id.
    const seen = new Set<string | number>()
    const occurrences: Occurrence[] = []

    for (const occurrence of batches.flat()) {
      if (!isPresent(occurrence.page?.path) || seen.has(occurrence.id)) continue
      seen.add(occurrence.id)
      occurrences.push(occurrence)
    }

    return occurrences
  }

  async move(
    occurrence: Occurrence | string | number,
    to: DateInput
  ): Promise<Occurrence> {
    if (!loggedIn()) throw notAuthenticated()

    const id = typeof occurrence === "object" ? occurrence?.id : occurrence
    if (!isPresent(id)) throw codedError("not_found", "Unknown occurrence")

    const starts_at = toStartsAt(to)

    try {
      const data = await ApiService.put(
        `/schedule/${encodeURIComponent(String(id))}`,
        { sdk: 1, starts_at }
      )
      eventManager.emit(MVT_EVENTS.SCHEDULE_CHANGED)
      return data.item
    } catch (err) {
      throw this.refusal(err, "Could not move this item")
    }
  }

  async add(
    path: string,
    on: DateInput,
    options: ScheduleAddOptions = {}
  ): Promise<Occurrence> {
    if (!loggedIn()) throw notAuthenticated()

    const starts_at = toStartsAt(on)
    const page_id = await this.pagePaths.resolve(path)

    try {
      const data = await ApiService.post("/schedule", {
        sdk: 1,
        page_id,
        starts_at,
        ...(options.isAllDay === undefined
          ? {}
          : { is_all_day: options.isAllDay })
      })
      eventManager.emit(MVT_EVENTS.SCHEDULE_CHANGED)
      return data.item
    } catch (err) {
      throw this.refusal(err, "Could not add this to your schedule")
    }
  }

  // Server refusals carry a code and a reason worth showing the member.
  private refusal(err: unknown, fallback: string): unknown {
    const body = (err as ApiError).response?.data as
      | { error?: string; reason?: string }
      | undefined

    return body?.error ? codedError(body.error, body.reason ?? fallback) : err
  }

  private async fetchAll(params: Record<string, any>): Promise<Occurrence[]> {
    const items: Occurrence[] = []
    let page = 1
    let totalPages = 1

    do {
      try {
        const data = await ApiService.get("/schedule", { ...params, page })
        if (data?.items) items.push(...data.items)
        totalPages = data?.pagination?.totalPages ?? 1
      } catch (err) {
        if ((err as ApiError).response?.status === 404) break
        throw err
      }
      page += 1
    } while (page <= totalPages)

    return items
  }
}

// $mvt.pages exposes a clean calendar vocabulary; the content engine keeps the
// fixed collection block's names. This map is that translation, done client-side.
const PAGES_CALENDAR_PARAM: Record<string, string> = {
  any: "any_schedule",
  global: "global_schedule",
  member: "member_schedule",
  own: "own_schedule"
}

const WITHIN_UNIT_SECONDS: Record<string, number> = {
  h: 3600,
  d: 86400,
  w: 604800
}

const MAX_PAGE_SIZE = 20
const DEFAULT_ORDER = "created_desc"

// A date-only value stays a date so the server resolves it in the member's timezone.
function toStartsAt(to: DateInput): string {
  if (typeof to === "string" && DATE_ONLY.test(to)) return to

  return toIsoDate(to, "date")
}

function withinSeconds(within: string): number {
  const match = /^(\d+)([hdw])$/.exec(within)
  if (!match) {
    throw codedError(
      "invalid_duration",
      `schedule.within expects "<n>h" | "<n>d" | "<n>w", got: ${within}`
    )
  }

  return Number(match[1]) * WITHIN_UNIT_SECONDS[match[2]]
}

// The content-plane sibling of $mvt.tables: queries the member-navigable page
// space through the content engine (GET /content/:type), so results are
// access-scoped pages with paths — never raw rows.
class Pages {
  async query(options: PagesQueryOptions = {}): Promise<PagesQueryResult> {
    const type = options.type ?? "any"

    try {
      const data = await ApiService.get(
        `/content/${encodeURIComponent(type)}`,
        this.toParams(options)
      )
      const items: Page[] = data?.items ?? []

      // Only legacy-model content can be path-less; drop it.
      return {
        results: items.filter(page => isPresent(page.path)),
        total: data?.total_count ?? 0
      }
    } catch (err) {
      const response = (err as ApiError).response
      const body = response?.data as
        | { reason?: string; message?: string }
        | undefined

      if (response?.status === 422 && body?.reason === "invalid_query") {
        throw codedError("invalid_query", body.message ?? "Invalid query")
      }
      if (response?.status === 404) return { results: [], total: 0 }
      throw err
    }
  }

  private toParams(options: PagesQueryOptions): Record<string, any> {
    // The context page is explicit (`of`); passing it alone means "children".
    const scope = options.scope ?? (options.of ? "children" : "app")

    // `sdk: 1` marks the strict path (loud validation, no global content-type
    // fallbacks) so the engine never infers it from the payload.
    const params: Record<string, any> = {
      sdk: 1,
      scope,
      order: options.order ?? DEFAULT_ORDER
    }

    if (options.of) params.of = options.of
    if (options.tags?.length) {
      params.with_tags = options.tags
      params.with_all_tags = options.match === "all"
    }
    if (options.where) params.where = options.where
    if (options.select?.length) params.select = options.select
    // The engine calls this a progress filter; `saved` is the author's word for
    // it, and keeps `progress` meaning completion the way $mvt.progress does.
    if (options.saved) params.progress = "saved"

    const schedule = options.schedule
    if (schedule) {
      params.direction = schedule.when
      if (schedule.within) params.duration = withinSeconds(schedule.within)
      if (schedule.calendar) {
        params.schedule_type =
          PAGES_CALENDAR_PARAM[schedule.calendar] ?? schedule.calendar
      }
      if (schedule.includeCurrent !== undefined) {
        params.include_current = schedule.includeCurrent
      }
      if (schedule.timezone) params.timezone = schedule.timezone
    }

    // The 20/page ceiling is the server's; paging rides limit + offset.
    const limit = Math.min(options.limit ?? MAX_PAGE_SIZE, MAX_PAGE_SIZE)
    params.limit = limit
    params.offset = limit * (Math.max(options.page ?? 1, 1) - 1)

    return params
  }
}

class Sdk {
  readonly MVT_EVENTS = MVT_EVENTS
  readonly tables: Record<string, Table>
  readonly pages: Pages
  readonly schedule: Schedule
  readonly native: NativeBridge
  readonly products: ProductsApi
  readonly progress: Progress
  readonly saved: Saved
  readonly userProperties: Record<string, unknown>
  private readonly stopRouteWatch: () => void

  constructor() {
    // Cached reads belong to a session, and a member switch ends one.
    readCache.reset()
    eventManager.on(MVT_EVENTS.LOGGED_IN, () => readCache.reset())
    eventManager.on(MVT_EVENTS.LOGGED_OUT, () => readCache.reset())

    const pagePaths = new PagePaths()

    this.pages = new Pages()
    this.schedule = new Schedule(pagePaths)
    this.native = new NativeBridge()
    this.progress = new Progress(pagePaths)
    this.saved = new Saved(pagePaths, this.pages)

    const cache: Record<string, Table> = {}
    this.tables = new Proxy(cache, {
      get(target, slug) {
        if (typeof slug !== "string") return undefined
        return target[slug] || (target[slug] = new Table(slug))
      }
    })

    this.products = new ProductsApi()

    const submittedProperties = (): Record<string, unknown> =>
      isPresent(vuexStore.getters["auth/currentUser"])
        ? vuexStore.getters[
            "subscriberProperties/subscriberSubmittedProperties"
          ]
        : {}

    this.userProperties = new Proxy(
      {},
      {
        get: (_target, key) => {
          if (typeof key !== "string") return undefined
          if (!isPresent(vuexStore.getters["auth/currentUser"])) return null

          return submittedProperties()[key] ?? null
        },
        has: (_target, key) =>
          typeof key === "string" && key in submittedProperties(),
        ownKeys: () => Reflect.ownKeys(submittedProperties()),
        getOwnPropertyDescriptor: (_target, key) =>
          typeof key === "string" && key in submittedProperties()
            ? {
                value: submittedProperties()[key],
                enumerable: true,
                configurable: true
              }
            : undefined,
        set: (_target, key, value) => {
          if (typeof key !== "string") return false
          if (!isPresent(vuexStore.getters["auth/currentUser"])) return true

          vuexStore
            .dispatch("subscriberProperties/setPropertyOptimistically", {
              slug: key,
              value
            })
            .catch((error: unknown) => {
              eventManager.emit(MVT_EVENTS.USER_PROPERTIES_ERROR, {
                key,
                value,
                error
              })
            })

          return true
        }
      }
    )

    const stopRouteWatch = router.afterEach(() =>
      eventManager.emit(MVT_EVENTS.ROUTE_CHANGED)
    )
    this.stopRouteWatch = () => stopRouteWatch()
  }

  destroy(): void {
    this.stopRouteWatch()
    this.native.destroy()
    this.progress.destroy()
    this.saved.destroy()
  }

  get events() {
    return eventManager
  }

  trackEvent(eventName: string, payload: TrackEventPayload = {}): void {
    const enrichedPayload: TrackEventPayload = {
      timestamp: Date.now(),
      ...payload
    }

    if (!("user" in enrichedPayload)) {
      enrichedPayload.user = this.user
    }

    eventManager.emit(eventName, enrichedPayload)
  }

  get user() {
    const user = vuexStore.getters["auth/currentUser"]

    return isPresent(user)
      ? {
          name: user.name,
          email: user.email,
          id: user.subscriberUuid
        }
      : null
  }

  get page() {
    const currentPage = (vuexStore as any).state?.pages?.currentPage || {}
    const pageData = currentPage.id
      ? vuexStore.getters["pages/getPageData"](currentPage.id) || {}
      : {}

    return {
      props: Object.freeze(
        pageDataToProps({
          pageData,
          builtInFieldContext: {
            pageTitle: currentPage.title,
            currentSchedule: currentPage.currentSchedule
          }
        })
      )
    }
  }

  async logout() {
    await vuexStore.dispatch("auth/logout")
  }

  async login() {
    router.push("#auth")
  }

  navigate(path: string, options: NavigateOptions = {}): void {
    if (isWebLink(path)) {
      window.open(path, "_blank")
      return
    }

    if (!path.startsWith("/") || path.startsWith("//")) {
      throw new Error(
        `$mvt.navigate expects an in-app path starting with "/", got: ${path}`
      )
    }

    const navigation = options.replace
      ? router.replace(path)
      : router.push(path)

    // Swallow redirected/duplicate navigation rejections (e.g. navigating to
    // the current path) so authored code never sees an unhandled rejection.
    navigation.catch(() => undefined)
  }

  get route(): MvtRoute {
    const { path, params, query } = router.currentRoute

    return { path, params, query }
  }
}

export default Sdk
